import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import SectionMessage from '@atlaskit/section-message';
import { setGlobalTheme } from '@atlaskit/tokens';
import { Provider as AccountsMappingProvider } from 'src/services/accountsMapping';
import { Provider as AssociatedServicesProvider } from 'src/services/associatedServices';
import { Provider as CurrentIssueProvider } from 'src/services/currentIssue';
import { Provider as DebugLogsProvider } from 'src/services/debugLogs';
import { Provider as UsersMappingProvider } from 'src/services/myUserMapping';
import { Provider as RulesProvider } from 'src/services/rules';
import store from 'src/store';
import storeInitializer from 'src/store/storeInitializer';

import jira from 'src/lib/api/jira';
import { BugsnagErrorBoundary } from 'src/lib/bugsnag';
import { getFeatures } from 'src/lib/featureFlags';
import { localStorage, sessionStorage } from 'src/lib/localStorage';
import { initializeResizer } from 'src/lib/resize';

import App from 'src/views';

import '@atlaskit/css-reset';

import { initializeAnalytics } from './lib/analytics';

// Initialize theme
setGlobalTheme({ colorMode: 'auto' });

// each function returns either component or null
// the first one wins and serves as a guard for other

const app = localStorageNotAvailable()
  || featureFlagsView()
  || redirectToDevEnv()
  || pagerDutyView()
  || jiraSideView();

const root = createRoot(document.getElementById('root'));
root.render(app);

function localStorageNotAvailable() {
  const isLocalStorageAvailable = localStorage.isAccessible() && sessionStorage.isAccessible();
  if (isLocalStorageAvailable) return null;

  const texts = [
    'We’re having trouble connecting to your account',
    'Please make sure your browser is configured to allow third-party cookies',
  ];
  return (
    <SectionMessage appearance="error">
      {texts.map((t, i) => <div key={i}>{t}</div>)}
    </SectionMessage>
  );
}

function redirectToDevEnv() {
  const { uiBranch, uiSha } = getFeatures();
  const urlParams = new URLSearchParams(window.location.search);
  let shouldUpdateUrl = false;

  function updateQueryParam(paramName, newValue) {
    if (newValue) {
      if (urlParams.get(paramName) !== newValue) {
        urlParams.set(paramName, newValue);
        shouldUpdateUrl = true;
      }
    } else if (urlParams.has(paramName)) {
      urlParams.delete(paramName);
      shouldUpdateUrl = true;
    }
  }

  updateQueryParam('branch', uiBranch);
  updateQueryParam('sha', uiSha);

  if (shouldUpdateUrl) {
    const newUrl = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}${window.location.hash}`;
    window.location.replace(newUrl);
    // let it render empty component until browser fetches scripts
    return <div></div>;
  }
  return null;
}

function pagerDutyView() {
  const isPagerDutyView = window.location.pathname.includes('/integration-jira-ui/pagerduty');
  if (!isPagerDutyView) return null;

  initializeAnalytics();

  return (
    <BugsnagErrorBoundary>
      <App/>
    </BugsnagErrorBoundary>
  );
}


function featureFlagsView() {
  const featureFlagsView = window.location.pathname.includes('/integration-jira-ui/feature_flags');
  if (!featureFlagsView) return null;

  return (
    <BugsnagErrorBoundary>
      <App/>
    </BugsnagErrorBoundary>
  );
}


function jiraSideView() {
  initializeAnalytics();
  storeInitializer(store);

  const searchParams = new URLSearchParams(window.location.search);
  const minHeight = (Number(searchParams.get('minHeight'))) || 150;

  initializeResizer(minHeight, jira.resize.bind(jira));

  return (
    <BugsnagErrorBoundary>
      <Provider store={store}>
        <AssociatedServicesProvider>
          <UsersMappingProvider>
            <CurrentIssueProvider>
              <AccountsMappingProvider>
                <RulesProvider>
                  <DebugLogsProvider>
                    <App/>
                  </DebugLogsProvider>
                </RulesProvider>
              </AccountsMappingProvider>
            </CurrentIssueProvider>
          </UsersMappingProvider>
        </AssociatedServicesProvider>
      </Provider>
    </BugsnagErrorBoundary>
  );
}
